/**
 * The cards the user applied as one conversation's next message carries them: one entry
 * per card, with the number the user sees on it, every paragraph it changed or added as a
 * whole paragraph before and after, the comments it carries, and each of its places an
 * Apply All run did not write.
 *
 * WHY the views read the paragraphs, not the card
 *   Before is the paragraph as this conversation's agent last read it, after is the
 *   paragraph as Word holds it now. A card re-placed on a paragraph that changed since it
 *   was proposed, two places of one change-throughout card in one paragraph, a card that
 *   landed only in part: each reads in the document exactly as it landed, where the card's
 *   own texts would say what it meant to write.
 *
 * WHY one entry per card
 *   One card can reach the views in several reports: an Apply All run writes each place of
 *   a change-throughout card as its own rule, and a card's own comments are told once
 *   they are written (`writeCardComments`). The user names the card, so the agent hears of
 *   the card once, with everything it wrote.
 *
 * WHY a card no read has reached yet is held back
 *   Every apply starts a read at once, but a message sent before that read lands would
 *   show the card's paragraphs from before its write. Send never waits on Word, so the
 *   card is held back whole and rides with the first message sent after a read reaches it.
 */
import type { AppliedCard, AppliedCardComment, AppliedCardParagraph, AppliedCardPlaceNotApplied, DocumentChange } from "@common/types/document-changes-for-agent";
import type { DocumentParagraph } from "@common/utils/redline/wire/document-paragraph";

import type { AppliedCardReport, AppliedCommentReport, AppliedEditReport, AppliedPlaceNotAppliedReport, AppliedReplyReport } from "./document-events";
import { DocumentView } from "./document-view";

/** An applied card's report, with the sequence of the event that carried it. */
export interface ReportedAppliedCard {
    sequence: number;
    appliedCard: AppliedCardReport;
}

/**
 * The reports of one card, gathered: its edit, every comment written for it, its reply,
 * and each of its places an Apply All run did not write, with the sequence of its newest
 * report and the reports themselves, held back together while no read has reached the
 * newest.
 *
 * NULL `edit`: the card wrote no edit (a comment card, a reply card).
 */
interface GatheredCard {
    cardNumber: number | null;
    newestSequence: number;
    reports: ReportedAppliedCard[];
    edit: AppliedEditReport | null;
    comments: AppliedCommentReport[];
    replies: AppliedReplyReport[];
    placesNotApplied: AppliedPlaceNotAppliedReport[];
}

/** The two views a conversation's entries are read from: what its agent last read, and the page now. */
export interface ViewsOfTheApply {
    conversationView: DocumentView;
    latestView: DocumentView;
}

/**
 * The applied cards one message carries, and the reports of each card it holds back
 * because no read has reached the card's newest report yet.
 */
export interface AppliedCardsForMessage {
    appliedCards: AppliedCard[];
    reportsHeldBack: ReportedAppliedCard[];
}

/**
 * Two edits of one card as one: every paragraph either names, once, and applied in part
 * when either part was.
 *
 * EXAMPLE
 *   place 1 on [_fees] whole, place 2 on [_fees, _term] whole -> [_fees, _term], appliedInPart false
 */
function joinedEdits(earlier: AppliedEditReport, later: AppliedEditReport): AppliedEditReport {
    return {
        ...earlier,
        paragraphBookmarkNames: Array.from(new Set([...earlier.paragraphBookmarkNames, ...later.paragraphBookmarkNames])),
        addedParagraphBookmarkNames: Array.from(new Set([...earlier.addedParagraphBookmarkNames, ...later.addedParagraphBookmarkNames])),
        tableChangeSentences: Array.from(new Set([...earlier.tableChangeSentences, ...later.tableChangeSentences])),
        appliedInPart: earlier.appliedInPart || later.appliedInPart,
    };
}

/**
 * One report added to its card's gathering.
 *
 * CASES
 *   - an edit              -> joined with the card's edit so far (`joinedEdits`)
 *   - a comment            -> one more comment of the card
 *   - a reply              -> one more reply of the card
 *   - a place not applied  -> one more place of the card the run did not write
 *   The card's number is the first number any report carries: a card comment's report
 *   may carry none while its card's edit does.
 */
function gatheredWith(gathered: GatheredCard, reported: ReportedAppliedCard): GatheredCard {
    const report = reported.appliedCard;
    const withIdentity = { ...gathered, cardNumber: gathered.cardNumber ?? report.cardNumber, newestSequence: Math.max(gathered.newestSequence, reported.sequence), reports: [...gathered.reports, reported] };
    if (report.kind === "edit") return { ...withIdentity, edit: withIdentity.edit === null ? report : joinedEdits(withIdentity.edit, report) };
    if (report.kind === "comment") return { ...withIdentity, comments: [...withIdentity.comments, report] };
    if (report.kind === "place_not_applied") return { ...withIdentity, placesNotApplied: [...withIdentity.placesNotApplied, report] };
    return { ...withIdentity, replies: [...withIdentity.replies, report] };
}

const NOTHING_GATHERED: Omit<GatheredCard, "newestSequence"> = { cardNumber: null, reports: [], edit: null, comments: [], replies: [], placesNotApplied: [] };

/**
 * The reports gathered card by card, in the order each card was first reported.
 *
 * CASES
 *   - a report naming a card (`cardId`) -> gathered with every other report of that card
 *   - a report naming none              -> a card of its own: a rule page card or a review's
 *                                          remedy is never joined to another
 *
 * EXAMPLE
 *   [edit msg-7 #match_a, edit msg-7 #match_b, comment msg-7, comment card msg-9]
 *   -> [msg-7: one edit over both places, one comment], [msg-9: its comment]
 */
function gatheredByCard(reports: ReportedAppliedCard[]): GatheredCard[] {
    const gatheredByKey = new Map<string, GatheredCard>();
    reports.forEach((reported, reportIndex) => {
        /**
         * A report naming no card is keyed by its place in the list, which no card id
         * can equal: card ids never start with "#".
         * EXAMPLE the third report, naming no card -> key "#2"
         */
        const cardKey = reported.appliedCard.cardId ?? `#${reportIndex}`;
        const gatheredSoFar = gatheredByKey.get(cardKey) ?? { ...NOTHING_GATHERED, newestSequence: reported.sequence };
        gatheredByKey.set(cardKey, gatheredWith(gatheredSoFar, reported));
    });
    return Array.from(gatheredByKey.values());
}

/**
 * Document order for a card's paragraphs: by where each stands now, or where it stood
 * when it is gone.
 *
 * EXAMPLE
 *   after at index 12, after at index 4, gone (before at 9) -> 4, 9, 12
 */
function documentOrderOf(paragraph: AppliedCardParagraph): number {
    return paragraph.after?.paragraphIndex ?? paragraph.before?.paragraphIndex ?? 0;
}

/**
 * Every paragraph an edit changed or added, before and after, in document order.
 *
 * CASES, per paragraph the edit names
 *   - in neither view                            -> left out: no text of it to show
 *   - read the same in both views                -> left out: the agent already reads it so
 *   - in the latest view only                    -> added: before is null
 *   - in the conversation's view only            -> gone: after is null
 *   - in both, reading differently               -> changed
 *
 * EXAMPLE
 *   edit on [_fees], adding [_audit]; the agent read "Fees are due in 30 days."
 *   -> [{ before "Fees are due in 30 days.", after "Fees are due in 45 days." },
 *       { before null, after "Supplier keeps auditable records." }]
 */
function paragraphsOfEdit(edit: AppliedEditReport, views: ViewsOfTheApply): AppliedCardParagraph[] {
    const namedBookmarks = Array.from(new Set([...edit.paragraphBookmarkNames, ...edit.addedParagraphBookmarkNames]));
    return namedBookmarks
        .flatMap((bookmarkName): AppliedCardParagraph[] => {
            const before = views.conversationView.paragraphWithBookmarkName(bookmarkName);
            const after = views.latestView.paragraphWithBookmarkName(bookmarkName);
            /** WHEN neither view holds the paragraph CONSEQUENCE there is no text to show. */
            if (before === null && after === null) return [];
            /** WHEN the agent already reads the paragraph as it stands CONSEQUENCE nothing to show. */
            if (before !== null && after !== null && DocumentView.readTheSame(before, after)) return [];
            return [{ before, after }];
        })
        .sort((left, right) => documentOrderOf(left) - documentOrderOf(right));
}

/** The paragraphs a comment sits on as the page reads them now, its comment among theirs. */
function paragraphsOfComment(comment: AppliedCommentReport, views: ViewsOfTheApply): DocumentParagraph[] {
    return comment.paragraphBookmarkNames.flatMap((bookmarkName) => {
        const paragraph = views.latestView.paragraphWithBookmarkName(bookmarkName);
        /** NULL: the paragraph is gone or its bookmark was dropped; the comment is named by its text alone. */
        return paragraph === null ? [] : [paragraph];
    });
}

/** A card's own comment as its edit's entry lists it: its text, the words it quotes, the paragraphs it sits on, and why it was not added when it was not. */
function cardCommentOf(comment: AppliedCommentReport): AppliedCardComment {
    return { text: comment.text, quotedText: comment.quotedText, paragraphBookmarkNames: comment.paragraphBookmarkNames, notWrittenReason: comment.notWrittenReason };
}

/** A card's place the run did not write as its edit's entry lists it: the paragraph it is in, not chosen or not applied. */
function placeNotAppliedOf(placeNotApplied: AppliedPlaceNotAppliedReport): AppliedCardPlaceNotApplied {
    return { paragraphBookmarkNames: placeNotApplied.paragraphBookmarkNames, outcome: placeNotApplied.outcome };
}

/**
 * The entries one gathered card becomes.
 *
 * CASES
 *   - it wrote an edit -> one edit entry, its comments inside it, a comment not added among them
 *                         with why, and each place the run did not write; applied in part when
 *                         its write was, or when a place of it was not written
 *   - it wrote none    -> one entry per comment written, then one per reply: a comment card, a
 *                         reply card; a comment not added is no card applied, so it is left out,
 *                         and so is a place not written when no place of its card wrote anything
 */
function entriesOfGatheredCard(gathered: GatheredCard, views: ViewsOfTheApply): AppliedCard[] {
    const edit = gathered.edit;
    if (edit !== null) {
        return [{
            kind: "edit",
            cardNumber: gathered.cardNumber,
            paragraphBookmarkNames: Array.from(new Set([...edit.paragraphBookmarkNames, ...edit.addedParagraphBookmarkNames])),
            appliedInPart: edit.appliedInPart || gathered.placesNotApplied.length > 0,
            paragraphs: paragraphsOfEdit(edit, views),
            tableChanges: edit.tableChangeSentences,
            comments: gathered.comments.map(cardCommentOf),
            placesNotApplied: gathered.placesNotApplied.map(placeNotAppliedOf),
        }];
    }
    const commentEntries = gathered.comments.filter((comment) => comment.notWrittenReason === null).map((comment): AppliedCard => ({
        kind: "comment",
        cardNumber: gathered.cardNumber,
        paragraphBookmarkNames: comment.paragraphBookmarkNames,
        text: comment.text,
        quotedText: comment.quotedText,
        paragraphs: paragraphsOfComment(comment, views),
    }));
    const replyEntries = gathered.replies.map((reply): AppliedCard => ({ kind: "reply", cardNumber: gathered.cardNumber, commentId: reply.commentId, text: reply.text }));
    return [...commentEntries, ...replyEntries];
}

/**
 * The cards the user applied as one conversation's next message carries them
 * (`gatheredByCard`, `entriesOfGatheredCard`), in the order each card was first applied,
 * and the reports of every card the latest read has not reached yet.
 *
 * CASES, per card
 *   - the latest view was read after its newest report -> carried, its paragraphs as they landed
 *   - it was read before                              -> held back whole: carried now, after would
 *                                                        show its paragraphs before the write
 *
 * EXAMPLE
 *   latest view covers 14; card #7 reported at 12, card #9's edit at 13 and its comment at 15
 *   -> appliedCards [#7], reportsHeldBack [#9's edit, #9's comment]
 */
export function appliedCardsForAgent(reports: ReportedAppliedCard[], views: ViewsOfTheApply): AppliedCardsForMessage {
    const gatheredCards = gatheredByCard(reports);
    return {
        appliedCards: gatheredCards.filter((gathered) => readAfterItsNewestReport(gathered, views.latestView)).flatMap((gathered) => entriesOfGatheredCard(gathered, views)),
        reportsHeldBack: gatheredCards.filter((gathered) => !readAfterItsNewestReport(gathered, views.latestView)).flatMap((gathered) => gathered.reports),
    };
}

/**
 * True when the latest view was read after the card's newest report, so it shows the card's
 * paragraphs as they landed.
 *
 * EXAMPLE
 *   newest report 13, latest view covers 14 -> true      newest report 15 -> false
 */
function readAfterItsNewestReport(gathered: GatheredCard, latestView: DocumentView): boolean {
    return gathered.newestSequence <= latestView.coversEventsThroughSequence;
}

/**
 * The paragraph changes left to list once the applied cards show theirs: a paragraph an
 * edit entry shows before and after is not listed a second time beside it.
 *
 * CASES, per change
 *   - a paragraph changed or added that an entry shows after   -> left out
 *   - a paragraph deleted that an entry shows before, gone     -> left out
 *   - any other change, renumbering and edits since the read   -> kept
 *
 * WHY by index
 *   An entry's after is a paragraph of the latest view and a change's after is too, so one
 *   paragraph has one index in both; a deletion's before is the conversation's view's.
 *
 * EXAMPLE
 *   changes [changed _fees (index 4), added _audit (index 5), changed _term (index 9)],
 *   card #7 shows _fees and _audit -> [changed _term]
 */
export function changesNotShownInAppliedCards(changes: DocumentChange[], appliedCards: AppliedCard[]): DocumentChange[] {
    const editParagraphs = appliedCards.flatMap((appliedCard) => (appliedCard.kind === "edit" ? appliedCard.paragraphs : []));
    const shownAfterIndexes = new Set(editParagraphs.flatMap((paragraph) => (paragraph.after === null ? [] : [paragraph.after.paragraphIndex])));
    const shownGoneIndexes = new Set(editParagraphs.flatMap((paragraph) => (paragraph.after === null && paragraph.before !== null ? [paragraph.before.paragraphIndex] : [])));
    return changes.filter((change) => {
        if (change.kind === "paragraph_changed" || change.kind === "paragraph_added") return !shownAfterIndexes.has(change.after.paragraphIndex);
        if (change.kind === "paragraph_deleted") return !shownGoneIndexes.has(change.before.paragraphIndex);
        return true;
    });
}
